import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutateAiPreferences, DEFAULT_META } from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'

/**
 * useAiPreferenceOp — add or remove an AI writing preference via the
 * server-side partial update endpoint. Same safety pattern as
 * useActionItemOp: optimistic cache, authoritative onSuccess,
 * rollback on error.
 *
 * Callers:
 *   addPref.mutate({ op: 'create', pref: { id, text, addedAt, addedBy },
 *                    optimistic: (list) => [...list, pref] })
 *   remove.mutate({ op: 'remove', id, optimistic: (list) => list.filter(...) })
 */
export function useAiPreferenceOp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ op, pref, id }) => {
      const items = await mutateAiPreferences({ op, pref, id })
      return items
    },

    onMutate: async ({ optimistic }) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      useHistoryStore
        .getState()
        .pushSnapshot(
          'AI preferences',
          queryClient.getQueryData(['goals']),
          previous
        )

      if (optimistic) {
        const current = previous.aiPreferences || []
        queryClient.setQueryData(['meta'], {
          ...previous,
          aiPreferences: optimistic(current),
        })
      }

      return { previous }
    },

    onSuccess: (items) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], { ...current, aiPreferences: items })
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
}
